/**
 * DS Coder — curated knowledge packs.
 *
 * Each pack carries: coding standards, common errors + fixes, and a curated
 * "golden stack" of libraries for a technology. Packs for the detected stack
 * are injected into the system prompt so generated code follows one uniform
 * style, avoids known failure modes, and only reaches for vetted packages.
 */

import type { KnowledgePack, TechId } from './types';

export const KNOWLEDGE_PACKS: Record<TechId, KnowledgePack> = {
  typescript: {
    id: 'typescript',
    label: 'TypeScript',
    docsLibraryId: 'microsoft/typescript',
    standards: [
      'Enable and respect strict mode; never introduce `any` — use `unknown` plus narrowing when the shape is uncertain.',
      'Prefer type inference for locals; write explicit types on exported function signatures and public APIs.',
      'Use discriminated unions instead of optional-field grab-bag interfaces for variant data.',
      'Prefer `interface` for object shapes that may be extended, `type` for unions/intersections/utility compositions.',
      'Never use non-null assertions (`!`) to silence the compiler; restructure so the check is visible to the type system.',
      'Use `import type { ... }` for type-only imports so bundlers can drop them.',
    ],
    commonErrors: [
      { error: '`Object is possibly undefined` errors silenced with `!`', fix: 'Guard with early return, optional chaining plus a default, or restructure so the value is provably present.' },
      { error: 'Enums generating unexpected runtime code / tree-shaking issues', fix: 'Prefer union-of-string-literal types or `as const` objects over `enum`.' },
      { error: 'Mutating readonly/props objects compiles in loose files', fix: 'Keep all files under strict mode; clone with spread before modifying data you did not create.' },
      { error: 'JSON.parse results typed as any leaking through the codebase', fix: 'Parse to `unknown` and validate with a schema (zod) before use.' },
    ],
    goldenLibraries: [
      { need: 'Runtime validation', use: 'zod', avoid: 'hand-rolled validators; joi (heavier, weaker TS inference)' },
      { need: 'Date/time', use: 'date-fns (tree-shakeable, immutable)', avoid: 'moment (deprecated, huge bundle)' },
      { need: 'Utility functions', use: 'native ES methods first; es-toolkit/lodash-es only for what natives lack', avoid: 'full lodash import (bundle bloat)' },
    ],
  },

  javascript: {
    id: 'javascript',
    label: 'JavaScript',
    standards: [
      'Use ES modules (`import`/`export`) exclusively; never mix with CommonJS `require` in the same package.',
      'Use `const` by default, `let` only when reassignment is required; never `var`.',
      'Always handle promise rejections — every `await` sits inside try/catch or the caller handles it.',
      'Use strict equality (`===`/`!==`) everywhere.',
    ],
    commonErrors: [
      { error: 'Floating promises (unawaited async calls) swallowing errors', fix: 'Await or explicitly `void promise.catch(...)`; enable no-floating-promises lint.' },
      { error: 'Using array index as React key or mutating state arrays in place', fix: 'Use stable ids as keys; produce new arrays with map/filter/spread.' },
      { error: '`forEach` with async callbacks not awaiting anything', fix: 'Use `for...of` with await, or `Promise.all(items.map(...))` for parallel work.' },
    ],
    goldenLibraries: [
      { need: 'HTTP requests', use: 'native fetch', avoid: 'axios unless interceptors are genuinely needed; request (deprecated)' },
      { need: 'Unique ids', use: 'crypto.randomUUID()', avoid: 'uuid package for simple cases' },
    ],
  },

  react: {
    id: 'react',
    label: 'React',
    docsLibraryId: 'reactjs/react.dev',
    standards: [
      'Function components + hooks only; no class components.',
      'Derive state where possible — do not mirror props into state; compute during render or with useMemo.',
      'Every useEffect must have a correct dependency array and a cleanup when it subscribes/schedules anything.',
      'Server data belongs in a query library (TanStack Query/SWR), not in useEffect + useState.',
      'Keep components small and single-purpose; lift state only as high as actually shared.',
      'Use stable, meaningful `key`s in lists (never array index for reorderable data).',
    ],
    commonErrors: [
      { error: 'Infinite re-render loop from setState during render or object/array deps recreated every render', fix: 'Move setState into handlers/effects; memoize object deps with useMemo/useCallback or restructure deps to primitives.' },
      { error: 'Stale closure — effect/handler reads old state', fix: 'Use functional updates (`setX(prev => ...)`) or include the value in the dependency array.' },
      { error: 'Fetching in useEffect without cancellation → setState on unmounted component / race conditions', fix: 'Use TanStack Query, or an AbortController cancelled in the effect cleanup.' },
      { error: 'Conditional hooks ("Rendered fewer hooks than expected")', fix: 'Call hooks unconditionally at the top level; branch inside the hook or after all hooks run.' },
    ],
    goldenLibraries: [
      { need: 'Server state / data fetching', use: '@tanstack/react-query', avoid: 'redux for server cache; ad-hoc useEffect fetching' },
      { need: 'Client state (when context is not enough)', use: 'zustand', avoid: 'redux boilerplate for small apps' },
      { need: 'Forms', use: 'react-hook-form + zod resolver', avoid: 'formik (heavier, slower)' },
      { need: 'Headless UI primitives', use: 'radix-ui / shadcn-ui', avoid: 'heavy kitchen-sink component suites for new code' },
    ],
  },

  nextjs: {
    id: 'nextjs',
    label: 'Next.js',
    docsLibraryId: 'vercel/next.js',
    standards: [
      'App Router: components are Server Components by default — add `"use client"` only where interactivity is required, as low in the tree as possible.',
      'Do data fetching in Server Components/route handlers with `fetch` caching options, not client-side effects.',
      'Never import server-only modules (db clients, secrets) into client components; mark them with `server-only`.',
      'Use `next/image` for images and `next/link` for internal navigation.',
      'Route handlers and server actions must validate input (zod) and return typed, explicit status codes.',
    ],
    commonErrors: [
      { error: '"useState/useEffect is not defined in Server Component" build errors', fix: 'Add `"use client"` to the interactive leaf component only — not to layouts/pages wholesale.' },
      { error: 'Hydration mismatch (Date.now, random values, locale formatting differing between server and client)', fix: 'Compute non-deterministic values in useEffect or pass them from the server as props.' },
      { error: 'Secrets leaking to the client via NEXT_PUBLIC_ env vars', fix: 'Only expose values that are truly public; keep secrets in server-only code paths.' },
      { error: 'Stale data from default fetch caching in App Router', fix: 'Set `cache: "no-store"` or `next: { revalidate: n }` explicitly per data requirement.' },
    ],
    goldenLibraries: [
      { need: 'Auth', use: 'next-auth / auth.js or the platform auth (e.g. Supabase Auth)', avoid: 'hand-rolled JWT/session handling' },
      { need: 'DB access', use: 'drizzle or prisma from server components / route handlers only' },
    ],
  },

  vite: {
    id: 'vite',
    label: 'Vite',
    docsLibraryId: 'vitejs/vite',
    standards: [
      'Client env vars must be prefixed `VITE_` and read via `import.meta.env`, never `process.env` in browser code.',
      'Use dynamic `import()` for route-level code splitting; keep the entry chunk lean.',
      'Static assets go in `public/` (served as-is) or are imported for hashing — do not hardcode `/src/...` URLs.',
      'Keep dev-server proxy config (`server.proxy`) as the single place API origins are mapped in development.',
    ],
    commonErrors: [
      { error: '`process is not defined` in browser code', fix: 'Use `import.meta.env.VITE_*`; add `define` in vite.config only for third-party libs that require process.env.' },
      { error: 'Env vars undefined in production build', fix: 'Ensure the `VITE_` prefix and that values are present at build time — they are inlined at build, not runtime.' },
      { error: 'Slow cold start / huge vendor chunk', fix: 'Use manualChunks or route-level dynamic imports; audit with rollup-plugin-visualizer.' },
    ],
    goldenLibraries: [
      { need: 'Testing', use: 'vitest + @testing-library/react', avoid: 'jest (duplicate transform pipeline in a Vite app)' },
    ],
  },

  vue: {
    id: 'vue',
    label: 'Vue 3',
    docsLibraryId: 'vuejs/docs',
    standards: [
      'Composition API with `<script setup>` for all new components.',
      'Props down, events up — mutate parent state only via emitted events or Pinia stores.',
      'Use `computed` for derived values instead of watchers where possible.',
    ],
    commonErrors: [
      { error: 'Losing reactivity by destructuring reactive objects', fix: 'Use `toRefs`/`storeToRefs` when destructuring reactive sources.' },
      { error: 'Watchers firing before data exists', fix: 'Guard inside the watcher or use `immediate`/`watchEffect` deliberately.' },
    ],
    goldenLibraries: [
      { need: 'State management', use: 'pinia', avoid: 'vuex (legacy)' },
      { need: 'Data fetching', use: '@tanstack/vue-query' },
    ],
  },

  svelte: {
    id: 'svelte',
    label: 'Svelte',
    docsLibraryId: 'sveltejs/svelte',
    standards: [
      'Svelte 5: use runes (`$state`, `$derived`, `$effect`) in new code; do not mix with legacy `$:` reactive statements in the same component.',
      'Keep load functions (`+page.ts`/`+page.server.ts`) as the data boundary in SvelteKit; components stay presentational.',
    ],
    commonErrors: [
      { error: 'Server-only code imported into universal load functions', fix: 'Put secret-touching logic in `+page.server.ts` / `$lib/server/`.' },
    ],
    goldenLibraries: [
      { need: 'Full-stack framework', use: 'SvelteKit' },
    ],
  },

  nodejs: {
    id: 'nodejs',
    label: 'Node.js',
    docsLibraryId: 'nodejs/node',
    standards: [
      'Async/await everywhere; no callback-style APIs in new code (promisify when wrapping legacy).',
      'Validate all external input at the boundary (zod) before it reaches business logic.',
      'Never block the event loop — offload CPU-heavy work (crypto, parsing huge payloads) or stream it.',
      'Read configuration from environment variables once at startup and fail fast when required values are missing.',
      'Log structured errors with context; never swallow exceptions silently.',
    ],
    commonErrors: [
      { error: 'Unhandled promise rejection crashing the process', fix: 'try/catch in every async entrypoint (route handler, job, cron) and a process-level rejection handler that logs.' },
      { error: 'Secrets committed via .env files', fix: 'Keep .env in .gitignore; provide .env.example with placeholder keys.' },
      { error: 'ESM/CJS interop failures ("require is not defined", default import mismatch)', fix: 'Pick one module system per package via `"type"` in package.json; use dynamic import() to load ESM from CJS.' },
    ],
    goldenLibraries: [
      { need: 'HTTP client', use: 'native fetch (Node 18+)', avoid: 'request (deprecated), got/axios unless streaming/retry features are needed' },
      { need: 'Env validation', use: 'zod (parse process.env at startup)' },
      { need: 'Testing', use: 'vitest or node:test' },
    ],
  },

  express: {
    id: 'express',
    label: 'Express',
    docsLibraryId: 'expressjs/express',
    standards: [
      'Routes stay thin: validate → call service function → map result to response. Business logic lives in services.',
      'Central error-handling middleware; async handlers wrapped so rejections reach it (express 5 or an asyncHandler wrapper).',
      'Always set explicit status codes and a consistent JSON error shape `{ error: string }`.',
      'Apply security middleware (helmet, CORS with explicit origins, rate limiting) at app setup.',
    ],
    commonErrors: [
      { error: 'Hanging requests from async route errors never reaching error middleware (Express 4)', fix: 'Wrap handlers: `const asyncHandler = fn => (req,res,next) => fn(req,res,next).catch(next)`.' },
      { error: '"Cannot set headers after they are sent"', fix: 'Ensure exactly one response per code path — return after res.json/res.send.' },
      { error: 'CORS wildcard `*` combined with credentials', fix: 'List allowed origins explicitly when cookies/Authorization headers are involved.' },
    ],
    goldenLibraries: [
      { need: 'Validation middleware', use: 'zod (parse req.body/query/params in a helper)' },
      { need: 'Security headers', use: 'helmet' },
    ],
  },

  tailwind: {
    id: 'tailwind',
    label: 'Tailwind CSS',
    docsLibraryId: 'tailwindlabs/tailwindcss.com',
    standards: [
      'Never build class names by string concatenation of fragments (`text-${color}-500`) — the JIT scanner cannot see them; use full class names in complete strings or a lookup map of full classes.',
      'Extract repeated utility clusters into components (or `cva` variants), not into @apply-heavy CSS.',
      'Use design tokens from the config/theme (colors, spacing) instead of arbitrary values except for true one-offs.',
      'Merge conditional classes with `clsx` + `tailwind-merge` (or the `cn` helper) so conflicting utilities resolve predictably.',
    ],
    commonErrors: [
      { error: 'Dynamically-built class names silently missing from the production build', fix: 'Map props to complete literal class strings: `{ red: "text-red-500", blue: "text-blue-500" }[color]`.' },
      { error: 'Conflicting utilities (`p-2 p-4`) applied unpredictably', fix: 'Run merged class lists through tailwind-merge.' },
    ],
    goldenLibraries: [
      { need: 'Variant-driven component classes', use: 'class-variance-authority (cva)' },
      { need: 'Class merging', use: 'clsx + tailwind-merge' },
    ],
  },

  supabase: {
    id: 'supabase',
    label: 'Supabase',
    docsLibraryId: 'supabase/supabase',
    standards: [
      'Every table gets Row Level Security enabled with explicit policies before it holds real data.',
      'The service-role key is server-only — never ship it to the browser; clients use the anon key + RLS.',
      'Check the `error` field on every supabase-js call; never assume `data` is non-null.',
      'Schema changes go through migration files, not ad-hoc dashboard edits.',
      'Long-running queries must respect statement timeouts — paginate and index instead of scanning.',
    ],
    commonErrors: [
      { error: 'Empty result sets that are actually RLS policy denials (no error thrown)', fix: 'When data is unexpectedly empty, verify policies for the current role first; test queries as the authenticated role, not service role.' },
      { error: '`.single()` throwing on 0 rows', fix: 'Use `.maybeSingle()` when absence is a valid state.' },
      { error: 'supabase.auth.getSession() used server-side for authorization', fix: 'Server code must verify the JWT with `auth.getUser(token)`; getSession trusts unverified client storage.' },
      { error: 'Vector/RAG queries timing out on large tables', fix: 'Use proper HNSW/IVF indexes, cap match counts, and filter by user/tenant columns before similarity ordering.' },
    ],
    goldenLibraries: [
      { need: 'Client', use: '@supabase/supabase-js v2' },
      { need: 'Type safety', use: 'supabase gen types typescript (generated Database types)' },
    ],
  },

  prisma: {
    id: 'prisma',
    label: 'Prisma',
    docsLibraryId: 'prisma/docs',
    standards: [
      'Single PrismaClient instance per process (global singleton in dev to survive hot reload).',
      'Use `select`/`include` deliberately — fetch only needed columns/relations.',
      'Schema changes via `prisma migrate dev` locally and `migrate deploy` in CI; never `db push` against production.',
    ],
    commonErrors: [
      { error: '"Too many connections" in serverless environments', fix: 'Use a pooled connection string (pgbouncer/Accelerate) and reuse one client per lambda container.' },
      { error: 'N+1 queries from lazy relation access in loops', fix: 'Batch with `include` or a single `findMany` + in-memory join.' },
    ],
    goldenLibraries: [
      { need: 'ORM', use: 'prisma (or drizzle for edge/lightweight needs)' },
    ],
  },

  drizzle: {
    id: 'drizzle',
    label: 'Drizzle ORM',
    docsLibraryId: 'drizzle-team/drizzle-orm-docs',
    standards: [
      'Define schema in code as the single source of truth; generate migrations with drizzle-kit and commit them.',
      'Use prepared statements for hot-path queries.',
      'Keep queries composable — build with the query builder rather than raw SQL strings except where SQL is genuinely clearer (then use the sql`` tag with bound params).',
    ],
    commonErrors: [
      { error: 'Schema drift between code and database', fix: 'Never hand-edit the DB; run `drizzle-kit generate` + migrate as the only path.' },
    ],
    goldenLibraries: [
      { need: 'Zod integration', use: 'drizzle-zod (derive insert/select schemas)' },
    ],
  },

  python: {
    id: 'python',
    label: 'Python',
    docsLibraryId: 'python/cpython',
    standards: [
      'Type hints on all public functions; run a type checker (mypy/pyright) in CI.',
      'Format with black/ruff-format and lint with ruff — no style debates in review.',
      'Use virtual environments + a lock file (uv/poetry/pip-tools); never install into the system interpreter.',
      'Prefer dataclasses/pydantic models over dicts for structured data crossing function boundaries.',
      'Use pathlib over os.path, and f-strings over % / .format().',
    ],
    commonErrors: [
      { error: 'Mutable default arguments (`def f(items=[])`) sharing state across calls', fix: 'Default to None and create the object inside the function.' },
      { error: 'Bare `except:` swallowing KeyboardInterrupt/SystemExit and hiding bugs', fix: 'Catch the narrowest exception type; log with `logger.exception` when re-raising is not possible.' },
      { error: 'Circular imports from module-level cross-imports', fix: 'Import inside functions, restructure shared types into a leaf module, or use `if TYPE_CHECKING:`.' },
    ],
    goldenLibraries: [
      { need: 'HTTP client', use: 'httpx (sync+async)', avoid: 'urllib for anything non-trivial' },
      { need: 'Data validation', use: 'pydantic v2' },
      { need: 'Package/env management', use: 'uv', avoid: 'bare pip + requirements.txt without locking' },
      { need: 'Testing', use: 'pytest' },
    ],
  },

  django: {
    id: 'django',
    label: 'Django',
    docsLibraryId: 'django/django',
    standards: [
      'Fat models / service layer, thin views; no business logic in templates.',
      'Every schema change through makemigrations/migrate, committed with the code that needs it.',
      'Use the ORM — reach for raw SQL only with parametrized queries and a comment explaining why.',
    ],
    commonErrors: [
      { error: 'N+1 queries in list views', fix: 'Use select_related/prefetch_related; assert query counts in tests.' },
      { error: 'DEBUG=True or SECRET_KEY hardcoded in production settings', fix: 'Environment-driven settings split (django-environ) with fail-fast checks.' },
    ],
    goldenLibraries: [
      { need: 'APIs', use: 'django-rest-framework or django-ninja' },
    ],
  },

  fastapi: {
    id: 'fastapi',
    label: 'FastAPI',
    docsLibraryId: 'fastapi/fastapi',
    standards: [
      'Pydantic models for every request and response body; set `response_model` explicitly.',
      'Use dependency injection (Depends) for auth, db sessions, and shared resources.',
      'Async endpoints must not call blocking IO — use async drivers or run_in_threadpool.',
    ],
    commonErrors: [
      { error: 'Blocking calls (requests, sync DB) inside `async def` freezing the event loop', fix: 'Use httpx.AsyncClient / async DB drivers, or declare the endpoint as plain `def` so it runs in the threadpool.' },
      { error: 'Returning ORM objects directly and leaking fields', fix: 'Always map through a response pydantic model.' },
    ],
    goldenLibraries: [
      { need: 'Server', use: 'uvicorn' },
      { need: 'DB', use: 'SQLAlchemy 2.0 async or SQLModel' },
    ],
  },

  go: {
    id: 'go',
    label: 'Go',
    docsLibraryId: 'golang/go',
    standards: [
      'Handle every error explicitly at the call site; wrap with `fmt.Errorf("context: %w", err)` for propagation.',
      'Accept interfaces, return concrete types; keep interfaces small and defined by the consumer.',
      'Pass context.Context as the first parameter through every request-scoped call chain.',
      'gofmt/goimports formatting is non-negotiable; vet + staticcheck in CI.',
    ],
    commonErrors: [
      { error: 'Goroutine leaks from missing cancellation', fix: 'Every goroutine must have a defined exit path — ctx.Done(), closed channel, or WaitGroup.' },
      { error: 'Loop-variable capture in goroutines (pre-1.22)', fix: 'Shadow the variable inside the loop body or upgrade to Go 1.22+ semantics.' },
    ],
    goldenLibraries: [
      { need: 'HTTP router', use: 'net/http (1.22+ patterns) or chi', avoid: 'heavy frameworks for simple services' },
    ],
  },

  rust: {
    id: 'rust',
    label: 'Rust',
    docsLibraryId: 'rust-lang/rust',
    standards: [
      'No `unwrap()`/`expect()` outside tests and provably-infallible cases — propagate with `?` and typed errors.',
      'Clippy on `-D warnings` in CI; rustfmt formatting always.',
      'Prefer borrowing over cloning; clone consciously with a comment when it is the right call.',
    ],
    commonErrors: [
      { error: 'Fighting the borrow checker with excessive clones/Rc<RefCell<>>', fix: 'Restructure ownership — split structs, pass slices, or use indices instead of references.' },
    ],
    goldenLibraries: [
      { need: 'Errors', use: 'thiserror (libraries) / anyhow (applications)' },
      { need: 'Async runtime', use: 'tokio' },
      { need: 'Serialization', use: 'serde' },
    ],
  },

  vercel: {
    id: 'vercel',
    label: 'Vercel (serverless)',
    docsLibraryId: 'vercel/vercel',
    standards: [
      'Respect the plan\'s serverless function limit — consolidate endpoints behind dispatcher routes (`?route=`) instead of adding top-level function files.',
      'Functions are stateless and short-lived: no in-memory caches that must survive requests, no background work after the response.',
      'Keep bundle size small; heavy deps belong in a single shared function, and dev/test files must be excluded (.vercelignore).',
    ],
    commonErrors: [
      { error: 'Deploy fails: "No more than 12 Serverless Functions" (Hobby plan)', fix: 'Fold the new endpoint into an existing dispatcher (index.ts with a route switch) instead of a new file under /api.' },
      { error: 'Work after res.json() being killed', fix: 'Await everything before responding, or move it to a queue/cron.' },
      { error: 'Function timeout on long operations', fix: 'Chunk the work with a job/queue pattern and poll from the client.' },
    ],
    goldenLibraries: [],
  },

  'react-query': {
    id: 'react-query',
    label: 'TanStack Query',
    docsLibraryId: 'tanstack/query',
    standards: [
      'Query keys are arrays that fully describe inputs (`["todos", { userId }]`); centralize them in a keys factory.',
      'Mutations invalidate or update affected queries in `onSuccess`/`onSettled` — never manually refetch from components.',
      'Set sensible `staleTime` per resource; do not leave everything at 0 and re-fetch on every focus.',
    ],
    commonErrors: [
      { error: 'Stale UI after a mutation', fix: 'queryClient.invalidateQueries with the exact key prefix, or optimistic update + rollback in onError.' },
      { error: 'Conditional fetching done with early returns breaking hooks order', fix: 'Use the `enabled` option instead of conditionally calling useQuery.' },
    ],
    goldenLibraries: [],
  },
};

export function getKnowledgePack(id: TechId): KnowledgePack | undefined {
  return KNOWLEDGE_PACKS[id];
}
